import {
  Box,
  Divider,
  Typography,
  Unstable_Grid2 as Grid,
  CircularProgress,
} from "@mui/material";
import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";
import { useRouter } from "src/hooks/use-router";
import { paths } from "src/paths";
import { PricingFaqs } from "src/sections/pricing/pricing-faqs";
import { PricingPlan } from "src/sections/pricing/pricing-plan";
import { PricingPlanIcon } from "src/sections/pricing/pricing-plan-icon";
import type { Page as PageType } from "src/types/page";
import { PlanType } from "src/types/strategy";
import { thunks } from "src/thunks/payment";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { tokens } from "src/locales/tokens";
import { useTranslation } from "react-i18next";
import { PaymentOptions, PlanPrice } from "src/types/payment";

// Add types and config
interface PlanConfig {
  name: string;
  description: string;
  features: string[];
  planType?: PlanType;
  productName?: string;
  icon: JSX.Element;
}

const PRICING_PLANS: PlanConfig[] = [
  {
    name: tokens.general.plans.free,
    description: tokens.pricing.plans.free.description,
    features: [
      tokens.pricing.plans.free.features.strategy,
      tokens.pricing.plans.free.features.products,
      tokens.pricing.plans.free.features.alerts
    ],
    planType: PlanType.Strategy,
    productName: "Strategy",
    icon: <PricingPlanIcon name="startup" />,
  },
  {
    name: tokens.general.plans.basic,
    description: tokens.pricing.plans.basic.description,
    features: [
      tokens.pricing.plans.basic.features.previous,
      tokens.pricing.plans.basic.features.reporting,
      tokens.pricing.plans.basic.features.history,
      tokens.pricing.plans.basic.features.users
    ],
    planType: PlanType.ContentPlan,
    productName: "Strategy and Content Plan",
    icon: <PricingPlanIcon name="standard" />,
  },
  {
    name: tokens.general.plans.professional,
    description: tokens.pricing.plans.professional.description,
    features: [
      tokens.pricing.plans.professional.features.previous,
      tokens.pricing.plans.professional.features.contacts,
      tokens.pricing.plans.professional.features.analytics,
      tokens.pricing.plans.professional.features.api,
      tokens.pricing.plans.professional.features.contracts
    ],
    icon: <PricingPlanIcon name="business" />,
  },
];

const Page: PageType = () => {
  usePageView();
  const router = useRouter();
  const { i18n, t } = useTranslation();
  const [paymentOptions, setPaymentOptions] = useState<
    PaymentOptions | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const plans = await thunks.getPlans(i18n.language);
        setPaymentOptions(plans);
      } catch (error) {
        toast.error(tokens.general.formError);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [i18n.language]);

  const formatPrice = useCallback(
    (planPrice: PlanPrice | undefined): string => {
      if (!planPrice) return "-";
      const { currency, price } = planPrice;
      const currencySymbol = currency === "UAH" ? "₴" : "$";
      return `${currencySymbol}${price.toLocaleString()}`;
    },
    []
  );

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Seo title={t(tokens.pricing.title)} />

      <Box
        sx={{
          "--Content-maxWidth": "var(--maxWidth-lg)",
          maxWidth: "var(--Content-maxWidth)",
          m: "var(--Content-margin)",
          p: "var(--Content-padding)",
          width: "var(--Content-width)",
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            mb: 4,
          }}
        >
          <Typography variant="h3">{t(tokens.pricing.upgradeTitle)}</Typography>
          <Typography
            color="text.secondary"
            sx={{ my: 2 }}
            variant="body1"
          >
            {t(tokens.pricing.subtitle)}
          </Typography>
          {/* <Stack
            alignItems="center"
            direction="row"
            spacing={1}
          >
            <Switch checked />
            <Typography variant="body1">Yearly Payment</Typography>
            <Chip
              color="primary"
              label="25% OFF"
              size="small"
            />
          </Stack> */}
        </Box>
        <Grid
          container
          spacing={4}
        >
          {PRICING_PLANS.map((plan, index) => {
            const planPrice =
              index === 1
                ? paymentOptions?.basic
                : index === 2
                  ? paymentOptions?.professional
                  : undefined;

            return (
              <Grid
                xs={12}
                md={4}
                key={plan.name}
              >
                <PricingPlan
                  cta={index === 0 ? "" : "Upgrade Now"}
                  description={t(plan.description)}
                  features={plan.features.map(feature => t(feature))}
                  icon={plan.icon}
                  name={t(plan.name)}
                  price={formatPrice(planPrice)}
                  popular={index === 1}
                  sx={{
                    height: "100%",
                    maxWidth: 460,
                    mx: "auto",
                  }}
                  onClick={
                    plan.planType && plan.productName
                      ? () => {
                          router.replace(
                            `${paths.payment.index}?planType=${plan.planType}&productName=${encodeURIComponent(plan.productName ?? "")}`
                          );
                        }
                      : undefined
                  }
                />
              </Grid>
            );
          })}
        </Grid>
        {/* <Box sx={{ mt: 4 }}>
          <Typography
            align="center"
            color="text.secondary"
            component="p"
            variant="caption"
          >
            30% of our income goes into Whale Charity
          </Typography>
        </Box> */}
      </Box>
      <Divider />
      <PricingFaqs />
    </>
  );
};

export default Page;
